import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Nav } from './nav.tsx';
import { Footer } from './footer.tsx';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';

type ParlaFunctions = {
  load: () => void;
  [key: `popup${number}`]: () => void;
}

type ParlaDoc = Document & {
  parla: ParlaFunctions;
}

export function AppointmentBooking() {
    const { lang } = useParams();
    const { t } = useTranslation();
    const baseURL = "https://www.parlaretail.com";
    useEffect(() => {
        if (lang) {
            i18next.changeLanguage(lang);
        }
    }, [lang]);


    return (
        <>
            <Helmet key={lang}>
                {/* General Meta */}
                <meta charSet="utf-8" />
                <meta http-equiv="X-UA-Compatible" content="IE=edge" />
                <title>{t('appointments.meta.title')}</title>
                <meta name="description" content={t('appointments.meta.description')} />
                <meta name="keywords" content={t('appointments.meta.keywords')} />
                <meta name="viewport" content="width=device-width, initial-scale=1" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={t('appointments.meta.title')} />
                <meta property="og:description" content={t('appointments.meta.description')} />
                <meta property="og:url" content={`${baseURL}/${lang}/`} />
                <meta property="og:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={t('appointments.meta.title')} />
                <meta name="twitter:description" content={t('appointments.meta.description')} />
                <meta name="twitter:image" content={`${baseURL}/img/sharing-image.jpg`} />

                {/* Hreflang for Language Versioning */}
                <link rel="alternate" hrefLang="en" href={`${baseURL}/en/`} />
                <link rel="alternate" hrefLang="es" href={`${baseURL}/es/`} />

                {/* Favicon */}
                <link rel="icon" type="image/png" href={`${baseURL}/img/parla-fav.png`} />
            </Helmet>
            <Nav />
            <div className="page">
                {/* Hero Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50">
                                    <div className="title f60 mf40 t-color-2 mb20">{t('appointments.hero.title')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {(t('appointments.hero.subtitle', { returnObjects: true }) as string[]).map((line, index) => (
                                            <div key={index}>{line}</div>
                                        ))}
                                    </div>
                                    <div className="btn-wrap">
                                        <button className="btn btn-dark" onClick={() => (document as ParlaDoc).parla.popup33()}>{t('appointments.hero.button')}</button>
                                    </div>
                                </div>
                                <div className="w50 v-center">
                                    <img src="/img/platform/Parla+schedule+appointments+image.png" alt={t('appointments.hero.image_alt')} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Appointment Scheduling Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50">
                                    <div className="title f40 mf40 t-color-3 mb20">{t('appointments.scheduling.title')}</div>
                                    <div className="title f60 mf40 t-color-2 mb20">{t('appointments.scheduling.subtitle')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {t('appointments.scheduling.description')}
                                        <ul className="ml50 mt20">
                                            {(t('appointments.scheduling.features', { returnObjects: true }) as { bold: string; text: string }[]).map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        <span className="bold">{feature.bold}</span> {feature.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                <div className="w50 v-center row p20 ml20">
                                    <img src="/img/platform/Parla+schedule+appointment+image.png" alt={t('appointments.scheduling.image_alt')} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Resource Management Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50 v-center row p20 ml20">
                                    <img src="/img/platform/Parla+management+roster+within+platform+for+appointments.png" alt={t('appointments.resources.image_alt')} />
                                </div>
                                <div className="w50">
                                    <div className="title f40 mf40 t-color-3 mb20">{t('appointments.resources.title')}</div>
                                    <div className="title f60 mf40 t-color-2 mb20">{t('appointments.resources.subtitle')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {t('appointments.resources.description')}
                                        <ul className="ml50 mt20">
                                            {(t('appointments.resources.features', { returnObjects: true }) as { bold: string; text: string }[]).map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        <span className="bold">{feature.bold}</span> {feature.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Automated Notifications Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50">
                                    <div className="title f40 mf40 t-color-3 mb20">{t('appointments.notifications.title')}</div>
                                    <div className="title f60 mf40 t-color-2 mb20">{t('appointments.notifications.subtitle')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {t('appointments.notifications.description')}
                                        <ul className="ml50 mt20">
                                            {(t('appointments.notifications.features', { returnObjects: true }) as { bold: string; text: string }[]).map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        <span className="bold">{feature.bold}</span> {feature.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                <div className="w50 v-center row p20 ml20">
                                    <img src="/img/platform/Parla+email+confirmation+for+appointments.png" alt={t('appointments.notifications.image_alt')} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Personalize Appointments Section */}
                <div className="section">
                    <div className="hero-how">
                        <div className="hero-text">
                            <div className="row">
                                <div className="w50 v-center row p20 ml20">
                                    <img src="/img/platform/Parla+customer+questions+when+schedule+appointment+image.png" alt={t('appointments.personalize.image_alt')} />
                                </div>
                                <div className="w50">
                                    <div className="title f40 mf40 t-color-3 mb20">{t('appointments.personalize.title')}</div>
                                    <div className="title f60 mf40 t-color-2 mb20">{t('appointments.personalize.subtitle')}</div>
                                    <div className="sub-title t-color-3 f20 mb20">
                                        {t('appointments.personalize.description')}
                                        <ul className="ml50 mt20">
                                            {(t('appointments.personalize.features', { returnObjects: true }) as { bold: string; text: string }[]).map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        <span className="bold">{feature.bold}</span> {feature.text}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Final Section */}
                <div className="section">
                    <div className="section80 row">
                        <div className="hero-side-r f50 mf30 w50">
                            <div className="title t-center">{t('appointments.final.title')}</div>
                            <div className="btn-wrap mt20 center-in p20">
                                <button className="btn btn-dark" onClick={() => (document as ParlaDoc).parla.popup33()}>{t('appointments.final.button')}</button>
                            </div>
                        </div>
                        <div className="hero-side-l w50">
                            <img src="/img/platform/AdobeStock_456174607.jpeg" alt={t('appointments.final.image_alt')} />
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
}
